import { Divider, Stack } from "@mui/material";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useUpdateEffect } from "react-use";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { useApi } from "../../containers/ApiContainer";
import { AutocompleteResultsContainer } from "../../containers/AutocompleteResultsContainer";
import { PlacementService } from "../../services/PlacementService";
import { Ramp, Stack as StackType } from "../../types";
import { usePermissions } from "../../hooks/usePermissions";
import { Permission } from "../../utils/permissions";
import { CurrentAircraft } from "../../widgets/CurrentAircraft/CurrentAircraft";
import { Layout } from "../../widgets/Layout";
import { useDimensions } from "../../widgets/Layout/hooks/useDimensions";
import { LayoutToolbar } from "../../widgets/LayoutToolbar";
import { TimelineAircraftList } from "../../widgets/TimelineAircraftList/TimelineAircraftList";
import { ZoomControl } from "../../widgets/ZoomControl";
import { AircraftFinder } from "./AircraftFinder";
import { AutoPark } from "./AutoPark";
import {
  CollapsibleRampActionCard,
  useLayout,
} from "./CollapsibleRampActionCard";
import { RampInfoCard } from "./RampInfoCard";
import { RampPrintLayout } from "./RampPrintLayout";

export const RAMP_BACKGROUND_COLORS = {
  reference: "#F4D2CD",
  testing: "#23967F",
};

type Props = {
  id: string;
  isReference?: boolean;
  isAlgorithmTesting?: boolean;
  // Direct props for ramp data (replaces useRampState)
  ramp: Ramp;
  setRamp: (ramp: Ramp) => void;
  history: {
    undo: () => void;
    redo?: () => void;
  };
  // Optional callback for scheduled view updates
  onScheduledUpdate?: () => void;
  // Flag to indicate if this is a scheduled view
  isScheduledView?: boolean;
  // Custom undo handler for scheduled views
  onUndo?: () => void;
  // Props for scheduled view functionality
  scheduledViewProps?: {
    tenantTripMap: Map<string, string>;
    placementService: PlacementService;
    onRemoveAircraft?: (tenantId: string, tripId: string) => void;
    currentTime: Date;
    onScheduledUpdate: () => void;
    onTimeChange?: (time: Date) => void;
    timeRange?: { start: Date; end: Date };
    allPlacements?: any[];
    allPlacementsForSearch?: any[];
    heatmapPlacements?: any[];
    onNavigateToLocation?: (
      locationId: string,
      locationType: string,
      time: Date
    ) => void;
  };
};

export const RampPresenterBase: React.FC<Props> = ({
  ramp,
  setRamp,
  history,
  onScheduledUpdate,
  isScheduledView = false,
  onUndo,
  scheduledViewProps,
}) => {
  const navigate = useNavigate();
  const { isDrawerOpen } = useLayout();
  const { activeFBO } = useActiveFBOs();
  const { hasPermission } = usePermissions();
  const { postgrest } = useApi();

  // Ramp data now comes from props instead of useRampState hook
  const [width, setWidth] = React.useState<number>(700);
  const [zoomLevel, setZoomLevel] = React.useState<number>(1);

  const div = React.useCallback(
    (node) => {
      const heightOffsetForRamp = 150;
      if (node?.offsetWidth && node?.offsetHeight) {
        const widthBasedOnAvaiableWidth = node.offsetWidth;
        const widthBasedOnAvailableHeight =
          (node.offsetHeight * ramp.width) / ramp.depth - heightOffsetForRamp;

        setWidth(
          Math.min(widthBasedOnAvaiableWidth, widthBasedOnAvailableHeight)
        );
      }
    },
    [isDrawerOpen, ramp.width, ramp.depth]
  );

  const zoomedWith = width * zoomLevel;

  const { feetToPixels } = useDimensions({
    location: { type: "ramp", ...ramp },
    width: zoomedWith,
    height: (zoomedWith * ramp.depth) / ramp.width,
  });

  const setStack = React.useCallback(
    (stack: StackType) => {
      setRamp({
        ...ramp,
        stack: {
          ...ramp.stack,
          ...stack,
        },
      });
    },
    [ramp, setRamp]
  );

  const positions = (ramp?.stack?.tenants ?? [])
    .map((t) => `${t.position.x},${t.position.y},${t.position.angle}`)
    .join("");

  useUpdateEffect(() => {
    setRamp({
      ...ramp,
      last_stacked: new Date().toISOString(),
    });
  }, [positions]);

  // Memoize placementsInRange to prevent creating new arrays on every render
  const placementsInRange = React.useMemo(() => {
    if (!scheduledViewProps?.allPlacements) {
      return [];
    }
    return scheduledViewProps.allPlacements.filter((p) => {
      const start = new Date(p.start_time).getTime();
      const end = new Date(p.end_time).getTime();
      const rangeStartTime = (
        scheduledViewProps?.timeRange?.start || new Date()
      ).getTime();
      const rangeEndTime = (
        scheduledViewProps?.timeRange?.end || new Date()
      ).getTime();
      return (
        (start >= rangeStartTime && start <= rangeEndTime) ||
        (end >= rangeStartTime && end <= rangeEndTime) ||
        (start <= rangeStartTime && end >= rangeEndTime)
      );
    });
  }, [
    scheduledViewProps?.allPlacements,
    scheduledViewProps?.timeRange?.start,
    scheduledViewProps?.timeRange?.end,
  ]);

  if (!ramp) {
    return null;
  }

  return (
    <AutocompleteResultsContainer>
      <Stack
        direction="row"
        sx={{
          width: "100%",
          height: "100%",
        }}
      >
        <CollapsibleRampActionCard
          location={{ type: "ramp", ...ramp }}
          onClickBack={() => navigate("/ramps")}
          hideNavigation={isScheduledView}
          extendBorderUp={false}
        >
          <Stack
            direction="column"
            spacing={1}
            sx={{
              maxHeight: "100%",
              width: isDrawerOpen ? 400 : 125,
            }}
          >
            <RampInfoCard
              isDrawerOpen={isDrawerOpen}
              ramp={ramp}
              isScheduledView={isScheduledView}
              timeRange={scheduledViewProps?.timeRange}
              allPlacements={scheduledViewProps?.allPlacements}
            />
            {ramp.autoparking_enabled && (
              <AutoPark
                ramp={ramp}
                stack={ramp.stack}
                setStack={setStack}
                isScheduledView={isScheduledView}
                placementService={scheduledViewProps?.placementService}
                currentTime={scheduledViewProps?.currentTime}
                postgrest={postgrest}
                fbo={activeFBO}
                onScheduledUpdate={scheduledViewProps?.onScheduledUpdate}
                isDrawerOpen={isDrawerOpen}
                onSetCurrentTime={scheduledViewProps?.onTimeChange}
              />
            )}
            {!isScheduledView && (
              <AircraftFinder
                mini={!isDrawerOpen}
                tenants={ramp.stack?.tenants ?? []}
                onSelect={(tenant) => {
                  setStack({
                    ...ramp.stack,
                    tenants: (ramp.stack?.tenants ?? []).map((t) => ({
                      ...t,
                      selected: t.id === tenant.id,
                    })),
                  });
                }}
              />
            )}

            <Divider />
            {isScheduledView && scheduledViewProps?.allPlacements ? (
              <TimelineAircraftList
                feetToPixels={feetToPixels}
                readOnly={!hasPermission(Permission.MODIFY_LAYOUT)}
                location={{ type: "ramp", ...ramp }}
                stack={ramp.stack}
                setStack={(stack) => {
                  setRamp({
                    ...ramp,
                    stack: {
                      ...ramp.stack,
                      ...stack,
                    },
                  });
                }}
                disableAutoOpen={!!onScheduledUpdate}
                isScheduledView={isScheduledView}
                onAddTrip={
                  scheduledViewProps?.onScheduledUpdate
                    ? () => {
                        // For scheduled views, trigger refresh after trip creation
                        scheduledViewProps.onScheduledUpdate();
                      }
                    : undefined
                }
                currentTime={scheduledViewProps?.currentTime}
                onRemoveAircraft={scheduledViewProps?.onRemoveAircraft}
                rangeStart={scheduledViewProps?.timeRange?.start || new Date()}
                rangeEnd={scheduledViewProps?.timeRange?.end || new Date()}
                placementsInRange={placementsInRange}
                allPlacementsFromNow={
                  scheduledViewProps.allPlacementsForSearch ||
                  scheduledViewProps.allPlacements
                }
                onRefresh={scheduledViewProps?.onScheduledUpdate}
                onTimeChange={scheduledViewProps?.onTimeChange}
                onNavigateToLocation={scheduledViewProps?.onNavigateToLocation}
                onSetCurrentTime={scheduledViewProps?.onTimeChange}
                rampShapes={ramp.shapes}
                rampDepth={ramp.depth}
              />
            ) : (
              <CurrentAircraft
                feetToPixels={feetToPixels}
                readOnly={!hasPermission(Permission.MODIFY_LAYOUT)}
                location={{ type: "ramp", ...ramp }}
                stack={ramp.stack}
                setStack={(stack) => {
                  setRamp({
                    ...ramp,
                    stack: {
                      ...ramp.stack,
                      ...stack,
                    },
                  });
                }}
                disableAutoOpen={!!onScheduledUpdate}
                isScheduledView={isScheduledView}
                onAddTrip={
                  scheduledViewProps?.onScheduledUpdate
                    ? () => {
                        // For scheduled views, trigger refresh after trip creation
                        scheduledViewProps.onScheduledUpdate();
                      }
                    : undefined
                }
                currentTime={scheduledViewProps?.currentTime}
                onRemoveAircraft={scheduledViewProps?.onRemoveAircraft}
              />
            )}
          </Stack>
        </CollapsibleRampActionCard>

        <Stack
          direction="column"
          sx={{
            flex: 1,
            height: "100%",
            width: "100%",
          }}
        >
          <LayoutToolbar
            location={{ type: "ramp", ...ramp }}
            setLocation={({ type: _type, ...location }) => {
              setRamp({ ...ramp, ...location });
            }}
            stack={ramp.stack}
            setStack={setStack}
            setStacks={() => {
              // Ramps don't have multiple stacks like hangars
            }}
            isScheduledView={isScheduledView}
            scheduledViewProps={scheduledViewProps}
            onClickUndo={() => {
              if (isScheduledView && onUndo) {
                onUndo();
              } else {
                history && history.undo();
              }
            }}
            renderOnPrint={() => (
              <RampPrintLayout ramp={ramp} stack={ramp.stack} />
            )}
          />
          <div
            ref={div}
            style={{
              flex: 1,
              position: "relative",
              height: "100%",
              margin: "20px",
            }}
          >
            <Layout
              width={zoomedWith}
              location={{ type: "ramp", ...ramp }}
              stack={ramp?.stack}
              setStack={setStack}
              onScheduledUpdate={onScheduledUpdate}
              scheduledViewProps={scheduledViewProps}
              options={{
                hideUnplaced: false,
                readOnly: false,
                frozen: !hasPermission(Permission.MODIFY_LAYOUT),
                printable: false,
                selectable: false,
              }}
              zoomLevel={zoomLevel}
            />
          </div>
        </Stack>
        <ZoomControl
          currentZoom={zoomedWith}
          increment={0.1}
          onClickPlus={(newZoomLevel) => setZoomLevel(newZoomLevel)}
          onClickMinus={(newZoomLevel) => setZoomLevel(newZoomLevel)}
        />
      </Stack>
    </AutocompleteResultsContainer>
  );
};
